import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import mixpanel from "mixpanel-browser";

import { selectUserCredits, selectUserUid } from "store/users/selectors";
import { transferLegacyCredits } from "store/payments/thunk";

import Button from "components/Button";
import Dialog from "components/Dialog";
import Divider from "components/Divider";
import FixedBanner from "components/FixedBanner";

import classes from "./style.module.scss";

import { VoltaBlueSmallLogo } from "~assets/images/icons/JSX/VoltaSmallBlueLogo";

const NewBalance = ({ userTotalLegacy = 0, userTotalCredits = 0, newTransferCredits = 0 }) => {
    const newBalance = userTotalCredits + newTransferCredits;

    return (
        <div className={classes.newBalanceContainer}>
            <div className={classes.detailsContainer}>
                <div className={classes.amountRow}>
                    <span className={classes.leftSpan}>Legacy credits balance</span>
                    <span>{userTotalLegacy}</span>
                </div>
                <div className={classes.amountRow}>
                    <span>New credits</span>
                    <span>{newTransferCredits}</span>
                </div>
            </div>
            <Divider />
            <div>
                <div className={classes.amountRow}>
                    <span className={classes.leftSpan}>Balance after transfer:</span>
                    <span>{newBalance}</span>
                </div>
            </div>
        </div>
    );
};

const LegacyTransfer = ({ onTransferSuccess, userCredits, transferLegacyCredits, userId }) => {
    const { total: userTotalLegacy = 0, credits: userTotalCredits = 0 } = userCredits || {};

    const newTransferCredits = useMemo(() => Math.ceil(userTotalLegacy / 2500), [userTotalLegacy]);

    const [openDrawer, setOpenDrawer] = useState(false);

    const handleTransfer = async () => {
        try {
            await transferLegacyCredits();
            mixpanel.track("Legacy Credits Convert", {
                UserId: userId,
                LegacyCredits: userTotalLegacy,
                NewCredits: newTransferCredits,
            });
            if (onTransferSuccess) {
                await onTransferSuccess();
            }
            setOpenDrawer(false);
        } catch (error) {
            console.error("Legacy credit transfer failed:", error);
        }
    };

    return (
        <>
            {userTotalLegacy > 0 && (
                <>
                    <FixedBanner type="warning">
                        <p>
                            Your account has <strong>{userTotalLegacy}</strong> legacy credits. You may spend these on{" "}
                            <strong>parametric analyses</strong> or <strong>transition them</strong> to the new credit
                            system.
                        </p>
                        <Button
                            onClick={() => setOpenDrawer(true)}
                            icon={() => <VoltaBlueSmallLogo fill="#fff" />}
                            type="orange"
                            small
                        >
                            Transfer Credits
                        </Button>
                    </FixedBanner>
                    <Dialog
                        open={openDrawer}
                        onClose={() => setOpenDrawer(false)}
                        className={classes.legacyTransferDialog}
                    >
                        <h1>Transfer Legacy Credits</h1>
                        <p>
                            Transfers from the legacy system to the new system are done at a rate of{" "}
                            <strong>2500 legacy credits</strong> to <strong>1 new credit</strong> rounded up to the
                            nearest whole number of new credits.
                        </p>
                        <p>
                            <strong>This transfer is irreversible.</strong>
                        </p>

                        <NewBalance
                            userTotalLegacy={userTotalLegacy}
                            userTotalCredits={userTotalCredits}
                            newTransferCredits={newTransferCredits}
                        />

                        <div className={classes.footer}>
                            <Button type="hollow" onClick={() => setOpenDrawer(false)}>
                                Cancel
                            </Button>
                            <Button onClick={handleTransfer}>Transfer</Button>
                        </div>
                    </Dialog>
                </>
            )}
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    userCredits: selectUserCredits,
    userId: selectUserUid,
});

const mapDispatchToProps = (dispatch) => ({
    transferLegacyCredits: () => dispatch(transferLegacyCredits()),
});

export default connect(mapStateToProps, mapDispatchToProps)(LegacyTransfer);
